// The API takes an Office document as base64 inside JSON. Built as one string (and then a second one
// by JSON.stringify), a 100 MB document cost the task pane ~1.7 GB and froze it for seconds; built as
// a Blob from base64 pieces it costs about four times the document (measured in Edge, 2026-10-31).
// The JSON is the same, byte for byte.

// A multiple of 3, so every piece but the last encodes without padding.
export const BODY_PIECE_BYTES = 3 * 1024 * 1024;

// withContent builds the request around the given placeholder for the document's base64.
export const toJsonBlobBody = async (bytes: Uint8Array, withContent: (content: string) => unknown): Promise<Blob> => {
  const placeholder = `docubird-content-${Math.random().toString(36).slice(2)}`;
  const around = JSON.stringify(withContent(placeholder)).split(`"${placeholder}"`);
  if (around.length !== 2) {
    throw new Error("The request must carry the document's content exactly once.");
  }

  const parts: BlobPart[] = [`${around[0]}"`];
  for (let offset = 0; offset < bytes.length; offset += BODY_PIECE_BYTES) {
    // Each piece moves into a Blob straight away, so the whole document never sits in one string.
    parts.push(new Blob([await toBase64(bytes.subarray(offset, offset + BODY_PIECE_BYTES))]));
  }
  parts.push(`"${around[1]}`);

  return new Blob(parts, { type: "application/json" });
};

// FileReader encodes off the main thread; a data URL is "data:<type>;base64,<content>".
const toBase64 = (bytes: Uint8Array): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const dataUrl = reader.result as string;
    resolve(dataUrl.slice(dataUrl.indexOf(",") + 1));
  };
  reader.onerror = () => reject(reader.error);
  // A document's bytes never live in a SharedArrayBuffer, which a Blob can't take.
  reader.readAsDataURL(new Blob([bytes as Uint8Array<ArrayBuffer>]));
});
